import { t } from "@lingui/macro";
import { ResumeDto } from "@reactive-resume/dto";
import { useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { LoaderFunction, redirect } from "react-router-dom";
import { Eye } from "lucide-react";

import { PAYMENT_DETAILS_KEY } from "@/client/constants/query-keys";
import { queryClient } from "@/client/libs/query-client";
import { findResumeById } from "@/client/services/resume";
import { getPaymentDetails } from "@/client/services/resume/payment";
import { useBuilderStore } from "@/client/stores/builder";
import { usePaymentStore } from "@/client/stores/payment";
import { useResumeStore } from "@/client/stores/resume";
import { useEditPreviewStore } from "@/client/stores/edit-preview";

export const BuilderPage = () => {
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const setFrameRef = useBuilderStore((state) => state.frame.setRef);

  const resume = useResumeStore((state) => state.resume);
  const title = useResumeStore((state) => state.resume.title);
  const previewOverlay = useResumeStore((state) => state.previewOverlay);

  const previewLabel = useEditPreviewStore((state) => state.label);

  const updateResumeInFrame = useCallback(() => {
    if (!frameRef || !frameRef.contentWindow) return;

    // Merge preview overlay if active
    let dataToSend = resume.data;
    if (previewOverlay) {
      const { path, value } = previewOverlay;

      // Parse path with array notation support (e.g., "items[0].name")
      const pathParts: Array<string | number> = [];
      path.split(".").forEach((part: string) => {
        const arrayMatch = part.match(/^(.+?)\[(\d+)\]$/);
        if (arrayMatch) {
          pathParts.push(arrayMatch[1], parseInt(arrayMatch[2], 10));
        } else {
          pathParts.push(part);
        }
      });

      // Deep clone to avoid mutation
      dataToSend = JSON.parse(JSON.stringify(resume.data));

      // Navigate and apply preview
      let current: any = dataToSend;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (current[part] === undefined) break;
        current = current[part];
      }
      const lastPart = pathParts[pathParts.length - 1];
      if (current && lastPart !== undefined) {
        current[lastPart] = value;
      }
    }

    const message = { type: "SET_RESUME", payload: dataToSend };
    (() => frameRef.contentWindow.postMessage(message, "*"))();
  }, [frameRef, resume.data, previewOverlay]);

  // Send resume data to iframe on initial load
  useEffect(() => {
    if (!frameRef) return;
    frameRef.addEventListener("load", updateResumeInFrame);
    return () => frameRef.removeEventListener("load", updateResumeInFrame);
  }, [frameRef]);

  // Send resume data to iframe on change of resume data or preview overlay
  useEffect(updateResumeInFrame, [updateResumeInFrame]);

  return (
    <div className="relative canvas-bg">
      <Helmet>
        <title>
          {title} - {t`theResume`}
        </title>
      </Helmet>

      {/* A suggested edit is being previewed on the page (Tailor tab hover) */}
      {previewLabel && (
        <div className="pointer-events-none fixed left-1/2 top-4 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full bg-sky-700 px-4 py-2 text-sm font-semibold text-white shadow-md animate-in fade-in slide-in-from-top-2 dark:bg-sky-400 dark:text-zinc-950">
          <Eye size={16} />
          <span>{t`Previewing: ${previewLabel}`}</span>
        </div>
      )}

      <iframe
        ref={setFrameRef}
        title={resume.id}
        src="/artboard/builder"
        className="w-full"
        style={{ height: "100vh" }}
      />

    </div>
  );
};

export const builderLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    const id = params.id as string;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () => findResumeById({ id }),
    });

    useResumeStore.setState({ resume });
    useResumeStore.temporal.getState().clear();

    try {
      const payment = await queryClient.fetchQuery({
        queryKey: PAYMENT_DETAILS_KEY,
        queryFn: () => getPaymentDetails(),
      });

      usePaymentStore.setState({ payment: payment });
    } catch (error) {
      // TODO: handle failure to fetch payment details
    }

    return resume;
  } catch (error) {
    return redirect("/dashboard");
  }
};
